/**
 * MyTasksView — Worker's "Meine Aufgaben" view.
 * Shows only tasks assigned to the current worker, grouped by day.
 * Includes pre-flight checklist (start task) and post-work form (complete task).
 * Phase 1 Sprint 4.
 *
 * Props: token, userId
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { AnimatedList } from './motion';
import FavoriteButton from './FavoriteButton';
import { toggleFavorite, listFavorites } from '../api/favorites';

const ACTIVITY_ICONS = {
  FERTILIZATION: '🌿', CROP_PROTECTION: '💧', TILLAGE: '🚜',
  SOWING: '🌱', HARVEST: '🌾', OTHER: '🧪',
};

const STATUS_LABEL_KEYS = {
  DRAFT: 'worker.statusDraft', SUBMITTED: 'worker.statusSubmitted', IN_PROGRESS: 'worker.statusInProgress',
  APPROVED: 'worker.statusApproved', POSTED: 'worker.statusPosted', ARCHIVED: 'worker.statusArchived',
};

const STATUS_COLORS = {
  DRAFT: 'text-[var(--aif-text-muted)]', SUBMITTED: 'text-green-400', IN_PROGRESS: 'text-blue-400',
  APPROVED: 'text-emerald-400', POSTED: 'text-purple-400',
};

export default function MyTasksView({ token, userId }) {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingTask, setStartingTask] = useState(null); // task for pre-flight
  const [completingTask, setCompletingTask] = useState(null); // task for post-work
  const [showDone, setShowDone] = useState(false);
  const [favoriteTaskIds, setFavoriteTaskIds] = useState(new Set());

  useEffect(() => {
    if (!token) return;
    listFavorites(token, 'task')
      .then(favs => setFavoriteTaskIds(new Set(favs.map(f => f.entity_id))))
      .catch(() => {});
  }, [token]);

  const handleTaskFavoriteToggle = useCallback(async (entityId, newState) => {
    setFavoriteTaskIds(prev => {
      const next = new Set(prev);
      newState ? next.add(entityId) : next.delete(entityId);
      return next;
    });
    try {
      await toggleFavorite(token, 'task', entityId);
    } catch {
      setFavoriteTaskIds(prev => {
        const next = new Set(prev);
        newState ? next.delete(entityId) : next.add(entityId);
        return next;
      });
    }
  }, [token]);

  const fetchTasks = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', sort: 'due_date', order: 'asc' });
      if (userId) params.set('assigned_to', userId);
      const res = await fetch(`/api/v1/aif/farm/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.items || data.tasks || []);
      }
    } catch (_) { /* network error — silent */ }
    setLoading(false);
  }, [token, userId]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Group tasks by day
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const todayTasks = tasks.filter(t => t.due_date === today && t.status !== 'APPROVED' && t.status !== 'POSTED');
  const tomorrowTasks = tasks.filter(t => t.due_date === tomorrow && t.status !== 'APPROVED' && t.status !== 'POSTED');
  const laterTasks = tasks.filter(t => t.due_date > tomorrow && t.status !== 'APPROVED' && t.status !== 'POSTED');
  const inProgressTasks = tasks.filter(t => t.status === 'IN_PROGRESS');
  const doneTasks = tasks.filter(t => t.status === 'APPROVED' || t.status === 'POSTED');

  const todayLabel = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-7 h-7 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden bg-[var(--aif-bg-page)]">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-xl font-semibold text-[var(--aif-text-primary)]">{t('worker.myTasks')}</h1>
        <p className="text-sm text-[var(--aif-text-muted)] mt-0.5">
          {todayTasks.length + inProgressTasks.length} {t('worker.forToday')} · {tomorrowTasks.length} {t('worker.forTomorrow')}
        </p>
      </div>

      {/* Scrollable content */}
      <div className="absolute left-0 right-0 bottom-0 overflow-y-auto overscroll-contain px-4 pb-8 space-y-4" style={{ top: 80 }}>
        {/* In Progress section */}
        {inProgressTasks.length > 0 && (
          <TaskSection title={t('worker.inProgress')} tasks={inProgressTasks}
            onStart={setStartingTask} onComplete={setCompletingTask}
            favoriteIds={favoriteTaskIds} onFavoriteToggle={handleTaskFavoriteToggle} />
        )}

        {/* Today */}
        {todayTasks.length > 0 && (
          <TaskSection title={`${t('weekly.today')} — ${todayLabel}`} tasks={todayTasks}
            onStart={setStartingTask} onComplete={setCompletingTask}
            favoriteIds={favoriteTaskIds} onFavoriteToggle={handleTaskFavoriteToggle} />
        )}

        {/* Tomorrow */}
        {tomorrowTasks.length > 0 && (
          <TaskSection title={t('weekly.tomorrow')} tasks={tomorrowTasks}
            onStart={setStartingTask} onComplete={setCompletingTask}
            favoriteIds={favoriteTaskIds} onFavoriteToggle={handleTaskFavoriteToggle} />
        )}

        {/* Later */}
        {laterTasks.length > 0 && (
          <TaskSection title={t('worker.later')} tasks={laterTasks}
            onStart={setStartingTask} onComplete={setCompletingTask}
            favoriteIds={favoriteTaskIds} onFavoriteToggle={handleTaskFavoriteToggle} />
        )}

        {/* Empty state */}
        {tasks.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-sm text-[var(--aif-text-muted)]">{t('worker.noTasks')}</p>
          </div>
        )}

        {/* Done section (collapsible) */}
        {doneTasks.length > 0 && (
          <div>
            <button onClick={() => setShowDone(!showDone)}
              className="w-full text-left text-xs font-medium text-[var(--aif-text-muted)] uppercase tracking-wider py-2 flex items-center gap-1">
              <span className="transform transition-transform" style={{ transform: showDone ? 'rotate(90deg)' : '' }}>▶</span>
              {t('worker.done')} ({doneTasks.length})
            </button>
            {showDone && (
              <AnimatedList className="space-y-2">
                {doneTasks.slice(0, 10).map(task => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </AnimatedList>
            )}
          </div>
        )}
      </div>

      {/* Pre-flight checklist bottom sheet */}
      {startingTask && (
        <PreFlightSheet task={startingTask} token={token}
          onClose={() => setStartingTask(null)}
          onStarted={() => { setStartingTask(null); fetchTasks(); }} />
      )}

      {/* Post-work form bottom sheet */}
      {completingTask && (
        <PostWorkSheet task={completingTask} token={token}
          onClose={() => setCompletingTask(null)}
          onCompleted={() => { setCompletingTask(null); fetchTasks(); }} />
      )}
    </div>
  );
}

function TaskSection({ title, tasks, onStart, onComplete, favoriteIds, onFavoriteToggle }) {
  return (
    <div>
      <div className="text-xs font-medium text-[var(--aif-text-muted)] uppercase tracking-wider mb-2">{title}</div>
      <AnimatedList className="space-y-2">
        {tasks.map(task => (
          <TaskCard key={task.id} task={task} onStart={onStart} onComplete={onComplete}
            favoriteIds={favoriteIds} onFavoriteToggle={onFavoriteToggle} />
        ))}
      </AnimatedList>
    </div>
  );
}

function TaskCard({ task, onStart, onComplete, favoriteIds, onFavoriteToggle }) {
  const { t } = useTranslation();
  const icon = ACTIVITY_ICONS[task.activity_type] || '📋';
  const statusColor = STATUS_COLORS[task.status] || 'text-[var(--aif-text-muted)]';
  const statusLabel = STATUS_LABEL_KEYS[task.status] ? t(STATUS_LABEL_KEYS[task.status]) : task.status;

  const details = [];
  if (task.product_name) details.push(task.product_name);
  if (task.planned_quantity && task.planned_unit) details.push(`${task.planned_quantity} ${task.planned_unit}`);
  if (task.field_name) details.push(task.field_name);

  return (
    <div className="bg-[var(--aif-bg-elevated)] rounded-xl p-4 border border-[var(--aif-border-default)]"
      data-testid={`worker-task-${task.id}`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{task.title}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <span className={`text-[10px] font-medium ${statusColor}`}>{statusLabel}</span>
          {onFavoriteToggle && (
            <FavoriteButton
              isFavorite={favoriteIds?.has(task.id) ?? false}
              onToggle={(newState) => onFavoriteToggle(task.id, newState)}
              size="sm"
            />
          )}
        </div>
      </div>

      {details.length > 0 && (
        <div className="text-xs text-[var(--aif-text-muted)] ml-7 mb-2">{details.join(' · ')}</div>
      )}

      {task.notes && (
        <div className="text-xs text-[var(--aif-text-muted)] ml-7 mb-2 truncate">{task.notes}</div>
      )}

      {/* Action buttons */}
      <div className="ml-7 flex gap-2 mt-2">
        {task.status === 'SUBMITTED' && onStart && (
          <button onClick={() => onStart(task)}
            className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition min-h-[44px]"
            data-testid="start-task-btn">
            ▶ {t('worker.start')}
          </button>
        )}
        {task.status === 'IN_PROGRESS' && onComplete && (
          <button onClick={() => onComplete(task)}
            className="px-4 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium hover:opacity-90 transition min-h-[44px]"
            data-testid="complete-task-btn">
            ✓ {t('worker.finish')}
          </button>
        )}
      </div>
    </div>
  );
}

function PreFlightSheet({ task, token, onClose, onStarted }) {
  const { t } = useTranslation();
  const [checks, setChecks] = useState({
    machine: false, implement: false, material: false, weather: false, gps: false,
  });
  const [saving, setSaving] = useState(false);

  const allChecked = Object.values(checks).every(Boolean);
  const toggle = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

  const handleStart = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/api/v1/aif/farm/tasks/${task.id}/start`, { pickup_checklist: checks }, { queueOnOffline: true });
      onStarted();
    } catch { /* ApiError */ }
    setSaving(false);
  };

  const items = [
    { key: 'machine', label: t('worker.checkMachine'), detail: task.machine_name || '' },
    { key: 'implement', label: t('worker.checkImplement'), detail: '' },
    { key: 'material', label: t('worker.checkMaterial'), detail: task.product_name ? `${task.product_name}: ${task.planned_quantity || '?'} ${task.planned_unit || 'kg'}` : '' },
    { key: 'weather', label: t('worker.checkWeather'), detail: '' },
    { key: 'gps', label: t('worker.checkGPS'), detail: '' },
  ];

  return (
    <div className="fixed inset-0 z-[var(--aif-z-modal)]" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-[var(--aif-bg-page)] rounded-t-2xl animate-slideUp max-h-[85vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('worker.startTitle')}</h2>
            <button onClick={onClose} className="text-[var(--aif-text-muted)] text-xl">×</button>
          </div>
          <div className="text-sm text-[var(--aif-text-muted)]">{task.title}</div>

          <div className="space-y-3" data-testid="preflight-checklist">
            {items.map(({ key, label, detail }) => (
              <button key={key} onClick={() => toggle(key)}
                className="w-full flex items-start gap-3 p-3 rounded-xl bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] text-left min-h-[56px]"
                data-testid={`check-${key}`}>
                <span className="text-lg mt-0.5">{checks[key] ? '☑' : '☐'}</span>
                <div>
                  <div className="text-sm font-medium text-[var(--aif-text-primary)]">{label}</div>
                  {detail && <div className="text-xs text-[var(--aif-text-muted)]">{detail}</div>}
                </div>
              </button>
            ))}
          </div>

          <button onClick={handleStart} disabled={!allChecked || saving}
            className="w-full py-3 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-40 min-h-[56px]"
            data-testid="confirm-start-btn">
            {saving ? t('worker.starting') : t('worker.confirmStart')}
          </button>
        </div>
      </div>
    </div>
  );
}

function PostWorkSheet({ task, token, onClose, onCompleted }) {
  const { t } = useTranslation();
  const [actualQty, setActualQty] = useState(task.planned_quantity ? String(task.planned_quantity) : '');
  const [actualArea, setActualArea] = useState(task.actual_area_ha ? String(task.actual_area_ha) : '45');
  const [returnQty, setReturnQty] = useState('0');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const inputCls = `w-full px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
    bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
    focus:outline-none focus-visible:ring-1 focus:ring-[var(--aif-color-primary)] min-h-[44px]`;

  const handleComplete = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/api/v1/aif/farm/tasks/${task.id}/complete`, {
        actual_quantity: parseFloat(actualQty) || null,
        actual_area_ha: parseFloat(actualArea) || null,
        return_quantity: parseFloat(returnQty) || 0,
        completion_notes: notes.trim() || null,
      }, { queueOnOffline: true });
      onCompleted();
    } catch { /* ApiError */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[var(--aif-z-modal)]" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-[var(--aif-bg-page)] rounded-t-2xl animate-slideUp max-h-[85vh] overflow-y-auto">
        <div className="p-6 space-y-4" data-testid="postwork-form">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--aif-text-primary)]">{t('worker.completeTitle')}</h2>
            <button onClick={onClose} className="text-[var(--aif-text-muted)] text-xl">×</button>
          </div>

          <div>
            <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('worker.actualArea')}</label>
            <div className="flex gap-2 items-center">
              <input type="number" value={actualArea} onChange={e => setActualArea(e.target.value)}
                className={inputCls} data-testid="actual-area" />
              <span className="text-sm text-[var(--aif-text-muted)]">ha</span>
            </div>
          </div>

          {task.product_name && (
            <>
              <div>
                <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('worker.actualQty')}</label>
                <div className="flex gap-2 items-center">
                  <input type="number" value={actualQty} onChange={e => setActualQty(e.target.value)}
                    className={inputCls} data-testid="actual-qty" />
                  <span className="text-sm text-[var(--aif-text-muted)]">{task.planned_unit || 'kg'}</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('worker.returnQty')}</label>
                <div className="flex gap-2 items-center">
                  <input type="number" value={returnQty} onChange={e => setReturnQty(e.target.value)}
                    className={inputCls} data-testid="return-qty" />
                  <span className="text-sm text-[var(--aif-text-muted)]">{task.planned_unit?.replace('/ha', '') || 'kg'}</span>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="text-xs text-[var(--aif-text-muted)] mb-1 block">{t('worker.notes')}</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={t('worker.notesPlaceholder')} rows={2}
              className={`${inputCls} resize-none`} />
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-secondary)] min-h-[56px]">
              {t('task.cancel')}
            </button>
            <button onClick={handleComplete} disabled={saving}
              className="flex-1 py-3 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-semibold disabled:opacity-50 min-h-[56px]"
              data-testid="confirm-complete-btn">
              {saving ? t('form.saving') : t('worker.confirmComplete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
